import React from "react"
import { ApolloConsumer } from "react-apollo"
import { Redirect } from "react-router-dom"
import BIError from "../BIError"
import UpsellModal from "../UpsellModal"
import GET_EMBED_TOKEN from "../../graphql/getEmbedToken.graphql"

import "./biModel.scss"

class BIModel extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      reportRendered: false,
      error: null,
    }
  }

  componentDidMount = () => {
    this.pbi = require("powerbi-client")
    this.fetchTokenAndEmbed()
  }

  isTokenValid = (embedToken) => {
    return (
      embedToken &&
      embedToken.token &&
      embedToken.expiration &&
      new Date(embedToken.expiration) > new Date()
    )
  }

  fetchTokenAndEmbed = async () => {
    if (!this.pbi || !powerbi || this.state.loading) return

    powerbi.reset(this.embedElement)

    let {
      book,
      chapter,
      fetchToken,
      dataset,
      isExcludeRespondentsView,
    } = this.props

    let reportItem = `${dataset}.${book}.${chapter}${
      isExcludeRespondentsView ? ".exclude" : ""
    }`.toLowerCase()

    this.setState({ loading: true, error: null })

    let embedToken = localStorage.getItem(reportItem)
    embedToken = embedToken ? JSON.parse(embedToken) : {}

    if (!this.isTokenValid(embedToken)) {
      let response = await fetchToken()

      if (response.error) {
        this.setState({
          loading: false,
          error: response.error,
        })
        return
      }

      embedToken = response.data.embedToken

      localStorage.setItem(reportItem, JSON.stringify(embedToken))

      if (!embedToken || !embedToken.id) {
        this.setState({
          loading: false,
          error: !embedToken ? "Internal server error" : embedToken.token,
        })
        return
      }
    }
    this.embedReport(embedToken)
  }

  embedReport = (embedToken) => {
    if (!this.isTokenValid(embedToken)) {
      this.setState(({ error }) => ({
        loading: false,
        error: error || "Invalid embed token.",
      }))
      return
    }

    let embedConfig = {
      type: "report",
      id: embedToken.id,
      embedUrl: "https://app.powerbi.com/reportEmbed",
      tokenType: this.pbi.models.TokenType.Embed,
      accessToken: embedToken.token,
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: true,
        panes: {
          pageNavigation: {
            visible: false,
          },
        },
      },
    }

    this.report = window.powerbi.load(this.embedElement, embedConfig)
    this.report.on("loaded", async () => {
      let searchParams = new URLSearchParams(this.props.location.search)
      const bookmark = searchParams.get("bookmark")

      if (bookmark) {
        this.report.render()
        this.setState({ loading: false })
        return
      }

      let pageName = this.props.page

      const pages = await this.report.getPages()
      const activePage = pages.find((page) => page.isActive)

      if (this.props.page) {
        const matchingPage = pages.find(
          (page) => page.displayName === this.props.page
        )

        if (matchingPage && !matchingPage.isActive) {
          matchingPage.setActive()
        }

        await this.report.setPage(matchingPage.name)
      }

      if (!pageName) {
        pageName = activePage.displayName
      }

      this.props.addPageQueryParam(pageName)
      this.report.render()
      this.setState({ loading: false })
    })

    // Define event handlers for Power BI report
    // loaded
    // saved
    // rendered
    // saveAsTriggered
    // error
    // dataSelected
    // buttonClicked
    // filtersApplied
    // pageChanged
    // commandTriggered
    // swipeStart
    // swipeEnd
    // bookmarkApplied
    // pageChanged
    this.report.on("rendered", this.onReportRendered)
    this.report.on("error", this.onReportError)
    this.report.on("dataSelected", this.props.onDataSelected)
  }

  onReportRendered = async () => {
    const pages = await this.report.getPages()
    const activePage = pages.find((page) => page.isActive)
    this.props.onReportRendered(this.report)

    this.props.addPageQueryParam(activePage.displayName)
  }

  onReportError = (event) => {
    this.setState({
      loading: false,
      error: event.detail,
    })
  }

  render = () => {
    let errorDetail = null

    if (this.state.error) {
      if (this.state.error === "Not Authenticated") {
        return (
          <Redirect
            to={{ pathname: "/login", state: { from: this.props.location } }}
          />
        )
      } else if (this.state.error === "Not Authorized") {
        errorDetail = <UpsellModal />
      } else {
        errorDetail = <BIError />
      }
    }

    return (
      <div className="model-embed-container">
        <div
          data-cy="model-embed-container"
          ref={(ref) => (this.embedElement = ref)}
        />
        {errorDetail}
      </div>
    )
  }
}

const BIModelWithData = (props) => {
  return (
    <ApolloConsumer>
      {(client) => {
        const fetchToken = () =>
          client.query({
            query: GET_EMBED_TOKEN,
            fetchPolicy: "network-only",
            variables: {
              book: props.book,
              chapter: props.chapter,
              isTutorial: props.isTutorial,
              dataset: props.dataset,
            },
          })

        return <BIModel {...props} fetchToken={fetchToken} />
      }}
    </ApolloConsumer>
  )
}

export default BIModelWithData
